import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { axiosErrorHandler } from 'common/helpers/axios.helper';
import { IAccountShort, IAddressShort, ISubdivisionShort, IUserShort } from 'common/models';
import { AppDispatch, RootState } from 'app/store';
import {
  IGroupedByDeliveryDateSupply,
  IGroupedSupplyListPayload,
  ISupply,
  IChangeSupplyStatusPayload,
  ICreateSupplyPayload,
  ISupplyListPayload,
  ISupplyParams,
  ISplitSupplyPayload,
  IUpdateSupplyDeliveryDatePayload,
  IUpdateSupplyPayload,
} from 'entities/Supply/Supply.models';
import { supplyTransport } from 'entities/Supply/Supply.transport';

interface IState {
  supply: ISupply | null;
  supplyList: ISupply[];
  supplyCount: number;
  isSupplyLoading: boolean;
  supplyBuyers: IAccountShort[];
  supplySubdivisions: ISubdivisionShort[];
  supplyUsers: IUserShort[];
  supplyAddresses: IAddressShort[];
  supplyGroupedList: IGroupedByDeliveryDateSupply[];
}

const initialState: IState = {
  supply: null,
  supplyList: [],
  supplyCount: 0,
  isSupplyLoading: false,
  supplyBuyers: [],
  supplySubdivisions: [],
  supplyUsers: [],
  supplyAddresses: [],
  supplyGroupedList: [],
};

export const supplySlice = createSlice({
  name: 'supply',
  initialState,
  reducers: {
    setSupply(state, action: PayloadAction<ISupply | null>) {
      state.supply = action.payload;
    },
    setSupplyList(state, action: PayloadAction<ISupply[]>) {
      state.supplyList = action.payload;
    },
    setSupplyListChunk(state, action: PayloadAction<ISupply[]>) {
      state.supplyList.push(...action.payload);
    },
    setSupplyCount(state, action: PayloadAction<number>) {
      state.supplyCount = action.payload;
    },
    setIsSupplyLoading(state, action: PayloadAction<boolean>) {
      state.isSupplyLoading = action.payload;
    },
    setSupplyBuyers(state, action: PayloadAction<IAccountShort[]>) {
      state.supplyBuyers = action.payload;
    },
    setSupplySubdivisions(state, action: PayloadAction<ISubdivisionShort[]>) {
      state.supplySubdivisions = action.payload;
    },
    setSupplyUsers(state, action: PayloadAction<IUserShort[]>) {
      state.supplyUsers = action.payload;
    },
    setSupplyAddresses(state, action: PayloadAction<IAddressShort[]>) {
      state.supplyAddresses = action.payload;
    },
    setSupplyGroupedList(state, action: PayloadAction<IGroupedByDeliveryDateSupply[]>) {
      state.supplyGroupedList = action.payload;
    },
    setSupplyGroupedListChunk(state, action: PayloadAction<IGroupedByDeliveryDateSupply[]>) {
      state.supplyGroupedList.push(...action.payload);
    },
  },
});

export const {
  setSupply,
  setSupplyList,
  setSupplyListChunk,
  setSupplyCount,
  setIsSupplyLoading,
  setSupplyBuyers,
  setSupplySubdivisions,
  setSupplyUsers,
  setSupplyAddresses,
  setSupplyGroupedList,
  setSupplyGroupedListChunk,
} = supplySlice.actions;

export const selectSupply = (state: RootState) => state.supply.supply;
export const selectSupplyList = (state: RootState) => state.supply.supplyList;
export const selectSupplyCount = (state: RootState) => state.supply.supplyCount;
export const selectIsSupplyLoading = (state: RootState) => state.supply.isSupplyLoading;
export const selectSupplyBuyers = (state: RootState) => state.supply.supplyBuyers;
export const selectSupplySubdivisions = (state: RootState) => state.supply.supplySubdivisions;
export const selectSupplyUsers = (state: RootState) => state.supply.supplyUsers;
export const selectSupplyAddresses = (state: RootState) => state.supply.supplyAddresses;
export const selectSupplyGroupedList = (state: RootState) => state.supply.supplyGroupedList;

export const supplyReducer = supplySlice.reducer;

export const getSupplyList = (payload: ISupplyListPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsSupplyLoading(true));

  try {
    const response = await supplyTransport.get(payload);
    dispatch(setSupplyList(response.data));
    dispatch(setSupplyCount(response.count));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsSupplyLoading(false));
  }
};

export const getSupplyListChunk = (payload: ISupplyListPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsSupplyLoading(true));

  try {
    const response = await supplyTransport.get(payload);
    dispatch(setSupplyListChunk(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsSupplyLoading(false));
  }
};

export const getSupplyById = (params: ISupplyParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsSupplyLoading(true));

  try {
    const response = await supplyTransport.getById(params.id);
    dispatch(setSupply(response));
    params.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsSupplyLoading(false));
  }
};

export const createSupply = (payload: ICreateSupplyPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsSupplyLoading(true));

  try {
    await supplyTransport.create(payload);
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsSupplyLoading(false));
  }
};

export const updateSupply = (payload: IUpdateSupplyPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsSupplyLoading(true));

  try {
    await supplyTransport.update(payload);
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsSupplyLoading(false));
  }
};

export const splitSupply = (payload: ISplitSupplyPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsSupplyLoading(true));

  try {
    await supplyTransport.split(payload);
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsSupplyLoading(false));
  }
};

export const changeSupplyStatus = (payload: IChangeSupplyStatusPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsSupplyLoading(true));

  try {
    await supplyTransport.changeStatus(payload);
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsSupplyLoading(false));
  }
};

export const updateSupplyDeliveryDate = (payload: IUpdateSupplyDeliveryDatePayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsSupplyLoading(true));

  try {
    await supplyTransport.updateDeliveryDate(payload);
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsSupplyLoading(false));
  }
};

export const getSupplyBuyers = (payload: ISupplyListPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsSupplyLoading(true));

  try {
    const response = await supplyTransport.getBuyers(payload);
    dispatch(setSupplyBuyers(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsSupplyLoading(false));
  }
};

export const getSupplySubdivisions = (payload: ISupplyListPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsSupplyLoading(true));

  try {
    const response = await supplyTransport.getSubdivisions(payload);
    dispatch(setSupplySubdivisions(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsSupplyLoading(false));
  }
};

export const getSupplyUsers = (payload: ISupplyListPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsSupplyLoading(true));

  try {
    const response = await supplyTransport.getUsers(payload);
    dispatch(setSupplyUsers(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsSupplyLoading(false));
  }
};

export const getSupplyAddresses = (payload: ISupplyListPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsSupplyLoading(true));

  try {
    const response = await supplyTransport.getAddresses(payload);
    dispatch(setSupplyAddresses(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsSupplyLoading(false));
  }
};

export const getGroupedSupplyList = (payload: IGroupedSupplyListPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsSupplyLoading(true));

  try {
    const response = await supplyTransport.getGroupedList(payload);
    dispatch(setSupplyGroupedList(response.data));
    dispatch(setSupplyCount(response.count));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsSupplyLoading(false));
  }
};

export const getGroupedSupplyListChunk = (payload: IGroupedSupplyListPayload) => async (dispatch: AppDispatch) => {
  try {
    const response = await supplyTransport.getGroupedList(payload);
    dispatch(setSupplyGroupedListChunk(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsSupplyLoading(false));
  }
};
